import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { HelpMissing, HelpService } from '../../services/help.service';
import { useToast } from '../../context/ToastContext';
import { formatDateTime } from '../../utils/dateUtils';

/** Platform admin view: help keys users opened with no text behind them, most-hit first (the to-write list). */
const MissingHelpPage: React.FC = () => {
    const toast = useToast();
    const [rows, setRows] = useState<HelpMissing[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                setRows(await HelpService.listMissing());
            } catch (error) {
                setLoadError(true);
                toast.showApiError(error, 'Failed to load missing help list');
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return rows
            .filter(r => !needle || r.key.toLowerCase().includes(needle) || r.path.toLowerCase().includes(needle))
            .sort((a, b) => b.hits - a.hits);
    }, [rows, search]);

    return (
        <Box sx={{ p: 3 }} data-help-page="platform.missingHelp.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Missing help texts</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }} data-help-section="platform.missingHelp.filters">
                <TextField
                    label="Search key or page"
                    data-help="platform.missingHelp.search"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: '1 1 240px', maxWidth: 360 }}
                />
            </Box>

            {loading && <LinearProgress aria-label="Loading" sx={{ mb: 1 }} />}
            <TableContainer component={Paper} data-help-section="platform.missingHelp.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="platform.missingHelp.col.key">Help key</TableCell>
                            <TableCell data-help="platform.missingHelp.col.page">Page</TableCell>
                            <TableCell align="right" data-help="platform.missingHelp.col.hits">Hits</TableCell>
                            <TableCell data-help="platform.missingHelp.col.first">First seen</TableCell>
                            <TableCell data-help="platform.missingHelp.col.last">Last seen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map(r => (
                            <TableRow key={`${r.key}|${r.path}`}>
                                <TableCell>{r.key}</TableCell>
                                <TableCell>{r.path}</TableCell>
                                <TableCell align="right">{r.hits.toLocaleString()}</TableCell>
                                <TableCell>{formatDateTime(r.firstSeen)}</TableCell>
                                <TableCell>{formatDateTime(r.lastSeen)}</TableCell>
                            </TableRow>
                        ))}
                        {!loading && visible.length === 0 && (
                            <TableRow><TableCell colSpan={5}>{loadError ? 'Could not load the list.' : 'No missing help texts found.'}</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default MissingHelpPage;
